
import { supabase } from "@/integrations/supabase/client";
import { DataTable } from "@/components/ui/data-table";
import { toast } from "sonner";
import { UserTableProps } from "./types";
import { UserNameColumn } from "./columns/UserNameColumn";
import { UserEmailColumn } from "./columns/UserEmailColumn";
import { BusinessTypeColumn } from "./columns/BusinessTypeColumn";
import { SubscriptionColumn } from "./columns/SubscriptionColumn";
import { ProductLimitCell } from "./ProductLimitCell";
import { StatusColumn } from "./columns/StatusColumn";
import { ActionsColumn } from "./columns/ActionsColumn";

export const UserTable = ({ owners, isLoading, onBlockUser }: UserTableProps) => {
  const handleDelete = async (userId: string) => {
    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (profileError) throw profileError;

      const { error: authError } = await supabase.functions.invoke('delete-user', {
        body: { userId }
      });

      if (authError) throw authError;

      toast.success("User deleted successfully");
      window.location.reload();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast.error("Error deleting user");
    }
  };

  const handleUpdateLimit = async (userId: string, newLimit: number) => {
    try {
      const { error: upsertError } = await supabase
        .from('custom_product_limits')
        .upsert({
          owner_id: userId,
          product_limit: newLimit
        });

      if (upsertError) throw upsertError;

      toast.success("Product limit updated successfully");
    } catch (error: any) {
      console.error("Error updating limit:", error);
      toast.error("Error updating limit");
    }
  };

  const columns = [
    {
      id: "name",
      label: "Name",
      cell: (row: any) => <UserNameColumn row={row} />,
    },
    {
      id: "email",
      label: "Email",
      cell: (row: any) => <UserEmailColumn row={row} />,
    },
    {
      id: "business_type",
      label: "Business Type",
      cell: (row: any) => (
        <BusinessTypeColumn businessTypeId={row.owner_data?.business_type_id} />
      ),
    },
    {
      id: "subscription",
      label: "Subscription",
      cell: (row: any) => <SubscriptionColumn userId={row.id} />,
    },
    {
      id: "product_limit",
      label: "Product Limit",
      cell: (row: any) => (
        <ProductLimitCell
          userId={row.id}
          subscriptionPlan={row.subscription_plan || 'free'}
          currentLimit={row.product_limit || 3}
          onUpdateLimit={handleUpdateLimit}
        />
      ),
    },
    {
      id: "status",
      label: "Status",
      cell: (row: any) => <StatusColumn row={row} />,
    },
    {
      id: "actions",
      label: "Actions",
      cell: (row: any) => (
        <ActionsColumn
          row={row}
          onDelete={handleDelete}
          onBlockUser={onBlockUser}
        />
      ),
    },
  ];

  return <DataTable columns={columns} data={owners} />;
};
